import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Styles/storysetup.css";
// import lessonGif from "../../../../../../../../assets/images/lessonGIF.gif";
import lessonGIFtransparent from "../../../../../../../../assets/images/lessonGIFtransparent.gif";
import { LowerSchoolStoryStatus } from "../LessonWebSocket/useLowerSchoolStorySocket";
import useImagesPreloaded from "../../../../../../../../hooks/useImagesPreloaded";
import ImportContactsOutlinedIcon from '@mui/icons-material/ImportContactsOutlined';
import { useNavigate } from "react-router";

/**
 * The story card itself — intro title card, narrated screens, options and the
 * Back control. Purely presentational: every frame comes from
 * `useLowerSchoolStorySocket`, so one card backs both surfaces:
 *   • teacher Student Preview — `StoryPreview`, preview mode, inside a modal
 *   • student attempt        — `LSLesson`, student mode, as a page
 *
 * `endSlot` is what shows on `story_end` — the student's submit card. Left out
 * (teacher preview) the loader stays up while the parent closes itself.
 */
const LowerSchoolStoryStage = ({
  status,
  intro,
  node,
  socketError,
  startStory,
  answerQuestion,
  goBack,
  /** Changing this clears any in-flight answer/back so a new session starts clean. */
  sessionKey,
  endSlot = null,
  /**
   * Drops the `.story-preview` card — its background art and the tab button —
   * and hands the whole area to the content instead. The student page runs
   * frameless; the teacher preview keeps the framed card.
   */
  frameless = false,
  /** Line under the loader gif, e.g. "Your story is on its way". */
  loaderCaption = "",
  /** Second, quieter line under the caption — what the backend is doing now. */
  loaderNote = "",
  /**
   * Reads each screen out as soon as it is shown. Off, the screen stays silent
   * until the speaker button is pressed, which then plays the question and runs
   * on into the options as usual. The teacher preview always autoplays; the
   * student page turns it off from Grade 2 up.
   */
  autoPlayNarration = true,
  /**
   * Rendered to the right of the top Back button (frameless only) — the student
   * page's "Full Screen" button. The teacher preview leaves it out.
   */
  headerAction = null,
  /** The student page is fullscreen — the top Back row drops down a little. */
  isFullscreen = false,
}) => {
  const audioRef = useRef(null);
  /** Set while an answer is in flight, cleared when the next frame lands. */
  const [isAnswering, setIsAnswering] = useState(false);
  /** Same, for a "back" — stops a second click before the previous screen lands. */
  const [isGoingBack, setIsGoingBack] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  /** Index of the word the narration is currently on, -1 before playback. */
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  /** Mirrors the audio element so the speaker icon matches what's audible. */
  const [isNarrationPlaying, setIsNarrationPlaying] = useState(false);
  /**
   * The option line being read out once the narration is done: index into
   * `optionAudioQueue`, -1 when nothing is queued or the run has finished.
   */
  const [activeOptionAudio, setActiveOptionAudio] = useState(-1);
  const [isOptionAudioPlaying, setIsOptionAudioPlaying] = useState(false);
  const optionAudioRef = useRef(null);
  /** The options grid — scrolled into view when its lines start being read. */
  const optionsRef = useRef(null);
  /**
   * Set from the moment an answer (or a Back) is sent until the next frame
   * lands. A media element's `ended` / `error` events arrive in their own task,
   * so one can still fire after the screen has been answered and would
   * otherwise start or advance the option run over the loader. A ref, not
   * state: the handlers have to see it the instant the click is handled.
   */
  const isScreenLeavingRef = useRef(false);
  /**
   * Set once "Back to course" is clicked and never cleared — unlike
   * `isScreenLeavingRef`, a frame landing mid-navigation must not re-arm
   * narration, answers or the start button.
   */
  const isExitingRef = useRef(false);
  const navigate = useNavigate();

  // Hold the screen on the loader until its artwork is downloaded: the intro's
  // background, or every option image on a story screen.
  const screenImageUrls = useMemo(() => {
    if (status === LowerSchoolStoryStatus.INTRO) {
      return intro?.lesson_image ? [intro.lesson_image] : [];
    }
    if (status === LowerSchoolStoryStatus.NODE) {
      return (node?.options ?? [])
        .map((option) => option.image_url)
        .filter(Boolean);
    }
    return [];
  }, [status, intro?.lesson_image, node]);

  const areImagesReady = useImagesPreloaded(screenImageUrls);

  // IDLE = connecting; INTRO_GENERATING = backend still building the title card.
  const isWaitingForIntro =
    !socketError &&
    (status === LowerSchoolStoryStatus.IDLE ||
      status === LowerSchoolStoryStatus.INTRO_GENERATING);
  const isWaitingForImages =
    !socketError &&
    (status === LowerSchoolStoryStatus.INTRO ||
      status === LowerSchoolStoryStatus.NODE) &&
    !areImagesReady;

  /**
   * Which screen is actually on show. While the loader is up nothing else is
   * rendered — no story layout, no background image — so it stays alone and
   * centred instead of being pushed around by the screen it's waiting for.
   */
  const isLoaderVisible =
    !socketError &&
    (isWaitingForIntro ||
      isWaitingForImages ||
      status === LowerSchoolStoryStatus.GENERATING ||
      (status === LowerSchoolStoryStatus.ENDED && !endSlot));
  const isStoryScreenVisible =
    status === LowerSchoolStoryStatus.NODE && !isLoaderVisible;

  // The lesson image backs the title card only — story screens are plain white,
  // and the loader shows on nothing at all.
  const backgroundImage =
    status === LowerSchoolStoryStatus.INTRO &&
    !isLoaderVisible &&
    intro?.lesson_image
      // ? `url("${intro.lesson_image}")`
      ? `${intro.lesson_image}`
      : undefined;

  // The next screen (or the end of the story) means the answer was accepted.
  useEffect(() => {
    if (
      status === LowerSchoolStoryStatus.NODE ||
      status === LowerSchoolStoryStatus.ENDED
    ) {
      setIsAnswering(false);
      setIsGoingBack(false);
      // The screen it was leaving for is here — narration is allowed again.
      // Cleared before the autoplay effect below runs, which is why this
      // effect stays declared first.
      isScreenLeavingRef.current = false;
    }
  }, [status, node]);

  // Each new session starts fresh.
  useEffect(() => {
    setIsAnswering(false);
    setIsGoingBack(false);
    isScreenLeavingRef.current = false;
  }, [sessionKey]);

  // Every frame is a new object, so this re-seeds the selection per screen:
  // a revisited screen comes back `answered` and re-highlights its answer.
  useEffect(() => {
    if (!node) return;
    setSelectedOptionId(node.answered ? node.student_answer_id ?? null : null);
  }, [node]);

  const questionId = node?.question_id;
  const substoryText = node?.substory ?? "";
  const options = node?.options ?? [];

  /**
   * The option lines to read out, in the order they're shown. Options are
   * narrated straight after the question so the child hears the choices without
   * having to read them; ones without audio are simply skipped.
   */
  const optionAudioQueue = useMemo(
    () =>
      (node?.options ?? [])
        .filter((option) => option.audio_url)
        // The id rides along so the option being read can be lit up as it
        // plays — options without audio are skipped, so the queue index on its
        // own doesn't line up with the options list.
        .map((option) => ({ id: option.id, url: option.audio_url })),
    [node]
  );

  /**
   * The option whose line is audible right now, `null` otherwise (including
   * while the run is paused) — it wears the same lift as a hover.
   */
  const narratingOptionId =
    isOptionAudioPlaying && activeOptionAudio >= 0
      ? optionAudioQueue[activeOptionAudio]?.id ?? null
      : null;

  // Audio and option images are generated after the text, so a node can arrive
  // with a status but no URL yet — show the wait instead of a dead element.
  const isPendingStatus = (statusValue) =>
    typeof statusValue === "string" &&
    ["pending", "generating", "in_progress", "processing", "queued"].includes(
      statusValue.toLowerCase()
    );
  const isAudioPending = !node?.audio_url && isPendingStatus(node?.audio_status);

  /**
   * Narration highlighting. The backend sends no word timings, so each word gets
   * a slice of the clip proportional to its length — close enough to read along,
   * and it self-corrects on every `timeupdate` because the boundaries are
   * fractions of the real duration rather than a fixed cadence.
   */
  const words = substoryText ? substoryText.split(/\s+/).filter(Boolean) : [];
  const wordBoundaries = useMemo(() => {
    if (!words.length) return [];
    const weights = words.map((word) => word.length + 1);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let consumed = 0;
    return weights.map((weight) => {
      consumed += weight;
      return consumed / totalWeight;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [substoryText]);

  const handleTimeUpdate = () => {
    const el = audioRef.current;
    if (!el || !el.duration || !wordBoundaries.length) return;
    const played = el.currentTime / el.duration;
    const index = wordBoundaries.findIndex((boundary) => played <= boundary);
    setActiveWordIndex(index === -1 ? words.length - 1 : index);
  };

  /** True while either clip is audible — that's what the speaker icon shows. */
  const isAudioPlaying = isNarrationPlaying || isOptionAudioPlaying;

  const toggleNarration = () => {
    if (isExitingRef.current) return;
    // Once the question has been read the button belongs to the option run.
    if (activeOptionAudio >= 0) {
      const optionEl = optionAudioRef.current;
      if (!optionEl) return;
      if (optionEl.paused) {
        optionEl.play().catch(() => {});
      } else {
        optionEl.pause();
      }
      return;
    }
    const el = audioRef.current;
    if (!el) {
      // No question narration on this screen — the button starts the options.
      if (optionAudioQueue.length) setActiveOptionAudio(0);
      return;
    }
    if (el.paused) {
      // A refused play() just rejects — the icon stays muted, which is the truth.
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  // Autoplay the narration for each new screen. "Start The Story" is a real user
  // gesture so this is normally allowed; if the browser still refuses, the
  // speaker button stays as the manual way in.
  useEffect(() => {
    setActiveWordIndex(-1);
    setActiveOptionAudio(-1);
    setIsOptionAudioPlaying(false);
    if (isExitingRef.current) return;
    // Hold the narration back until the option artwork is in — the images are
    // what the screen is still waiting on, and the clip should start with them
    // rather than play to a loader. The effect re-runs when they land.
    if (!areImagesReady) return;
    // Manual narration: the speaker button is the only way in.
    if (!autoPlayNarration) return;
    const el = audioRef.current;
    if (!el || !node?.audio_url) {
      // A screen with no question audio still reads its options out.
      if (optionAudioQueue.length) setActiveOptionAudio(0);
      return;
    }
    el.currentTime = 0;
    const played = el.play();
    // If the browser refuses autoplay the button shows the muted icon, which is
    // then the way in.
    if (played?.catch) played.catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.node_key, node?.audio_url, areImagesReady, autoPlayNarration]);

  // Drives the option run: one clip per index, straight on from the question.
  useEffect(() => {
    const el = optionAudioRef.current;
    if (!el) return;
    if (
      isExitingRef.current ||
      isScreenLeavingRef.current ||
      activeOptionAudio < 0 ||
      activeOptionAudio >= optionAudioQueue.length
    ) {
      el.pause();
      return;
    }
    // A fresh `src` starts at 0 on its own — seeking before it loads would only
    // risk an InvalidStateError.
    el.src = optionAudioQueue[activeOptionAudio].url;
    const played = el.play();
    if (played?.catch) played.catch(() => {});
  }, [activeOptionAudio, optionAudioQueue]);

  // The question has been read and the options are starting — bring them into
  // view so the child is looking at the choice being read out. Only on the
  // first clip of the run: they are all on screen together, so re-scrolling
  // per clip would just fight the reader. `nearest` is the smallest scroll
  // that makes the grid fully visible, and none at all when it already is, so
  // the passage above is left in place wherever there is room for both.
  useEffect(() => {
    if (activeOptionAudio !== 0) return;
    // Next frame: on a screen with no question audio the run starts in the
    // same commit the grid mounts in, so let layout settle first.
    const frame = requestAnimationFrame(() => {
      optionsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeOptionAudio]);

  // The card can go away mid-clip too — the teacher closes the preview modal,
  // the student leaves the page. React runs an effect's cleanup before it
  // detaches the host refs below it, so this is the last point at which the
  // elements can still be reached to be silenced.
  useEffect(
    () => () => {
      audioRef.current?.pause();
      optionAudioRef.current?.pause();
    },
    []
  );

  /**
   * Silences the screen for good and blocks the option run from starting or
   * advancing again — the choice has been made, so there is nothing left to
   * read out. Both elements are paused by hand rather than left to the
   * option-run effect: the answer takes the screen down with it, and a
   * detached media element goes on playing in some browsers, so the pause has
   * to land while the screen is still up.
   */
  const stopNarration = () => {
    isScreenLeavingRef.current = true;
    audioRef.current?.pause();
    optionAudioRef.current?.pause();
    setActiveOptionAudio(-1);
    setIsOptionAudioPlaying(false);
    setIsNarrationPlaying(false);
  };

  /** Question audio done → hand over to the first option clip. */
  const handleNarrationEnded = () => {
    setIsNarrationPlaying(false);
    setActiveWordIndex(words.length - 1);
    // A question clip that ends just as the answer goes out must not kick the
    // options off behind the loader.
    if (isScreenLeavingRef.current) return;
    if (optionAudioQueue.length) setActiveOptionAudio(0);
  };

  /** Each option clip chains into the next; the last one ends the run. */
  const handleOptionAudioEnded = () => {
    setIsOptionAudioPlaying(false);
    if (isScreenLeavingRef.current) return;
    setActiveOptionAudio((index) =>
      index >= 0 && index + 1 < optionAudioQueue.length ? index + 1 : -1
    );
  };

  const handleAnswer = (optionId) => {
    if (isExitingRef.current || isAnswering) return;
    stopNarration();
    setSelectedOptionId(optionId);
    if (answerQuestion?.(questionId, optionId)) {
      setIsAnswering(true);
    } else {
      setSelectedOptionId(null);
    }
  };

  /**
   * Leaves the story. The click is swallowed here so no ancestor/overlay
   * handler (e.g. the full-card "Start the story" button) runs, audio is
   * silenced, and `isExitingRef` blocks every other handler until unmount.
   */
  const handleBackToCourse = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    event?.nativeEvent?.stopImmediatePropagation?.();
    if (isExitingRef.current) return;
    isExitingRef.current = true;
    stopNarration();
    navigate(-1);
  };

  /** "Start The Story" — ignored once the user is leaving. */
  const handleStartStory = () => {
    if (isExitingRef.current) return;
    startStory?.();
  };

  // Nothing precedes the first question, so there is nowhere to go back to.
  // An advance-only (consequence) screen has no `question_id`, but it always
  // follows the choice that caused it — so Back is always available there.
  const canGoBack = Boolean(node?.advance_only) || Number(questionId) > 1;

  /** The gif plus its captions — every waiting state shows the same thing. */
  const loader = (
    <div className="story-preview-loader">
      <img className="story-preview-loader-gif" src={lessonGIFtransparent} alt="" />
      {/* {loaderCaption && (
        <span className="story-preview-loader-title">{loaderCaption}</span>
      )} */}
      {loaderNote && (
        <span className="story-preview-loader-note">{loaderNote}</span>
      )}
    </div>
  );

  /** Back steps the story server-side — the reply is a normal `node` frame. */
  const handleBack = () => {
    if (isExitingRef.current || !canGoBack || isAnswering || isGoingBack) return;
    stopNarration();
    if (goBack?.()) {
      setIsGoingBack(true);
    }
  };

  return (
    <div className={frameless ? "story-preview-frameless" : "story-preview"}>
      {/* the card's tab button belongs to the frame — frameless drops it */}
      {/* {!frameless && (
      <span className="story-tab-btn">
        <svg
          width="54"
          height="54"
          viewBox="0 0 54 54"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M52.583 26.4871C52.583 12.3033 41.0847 0.804989 26.9009 0.80499C12.717 0.804991 1.21875 12.3033 1.21875 26.4871C1.21875 40.671 12.717 52.1693 26.9009 52.1693C41.0847 52.1693 52.583 40.671 52.583 26.4871Z"
            fill="url(#paint0_linear_4266_201809)"
          />
          <path
            d="M52.1062 26.7255C52.1062 12.6733 40.7146 1.28183 26.6625 1.28183C12.6103 1.28183 1.21875 12.6733 1.21875 26.7255C1.21875 40.7777 12.6103 52.1693 26.6625 52.1693C40.7146 52.1693 52.1062 40.7777 52.1062 26.7255Z"
            fill="url(#paint1_linear_4266_201809)"
          />
          <path
            d="M31.8534 5.10966C19.9024 2.28768 7.92645 9.68828 5.10447 21.6393C2.28249 33.5904 9.68311 45.5663 21.6342 48.3882C33.5852 51.2102 45.5611 43.8097 48.3831 31.8586C51.2051 19.9076 43.8045 7.93164 31.8534 5.10966Z"
            fill="#020202"
          />
          <path
            d="M31.6969 5.77494C20.1134 3.03976 8.50585 10.2127 5.77066 21.7962C3.03548 33.3797 10.2084 44.9872 21.7919 47.7224C33.3754 50.4576 44.9829 43.2846 47.7181 31.7012C50.4533 20.1177 43.2804 8.51013 31.6969 5.77494Z"
            fill="#141414"
          />
          <defs>
            <linearGradient
              id="paint0_linear_4266_201809"
              x1="45.0611"
              y1="44.6474"
              x2="8.74041"
              y2="8.32671"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#333333" />
              <stop offset="0.0149" stopColor="#393939" />
              <stop offset="0.1857" stopColor="#757575" />
              <stop offset="0.331" stopColor="#A0A0A0" />
              <stop offset="0.4437" stopColor="#BBBBBB" />
              <stop offset="0.51" stopColor="#C5C5C5" />
              <stop offset="0.5747" stopColor="#BABABA" />
              <stop offset="0.6874" stopColor="#9E9E9E" />
              <stop offset="0.8346" stopColor="#6F6F6F" />
              <stop offset="1" stopColor="#333333" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_4266_201809"
              x1="8.67055"
              y1="44.7175"
              x2="44.6538"
              y2="8.73409"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#4D4D4D" />
              <stop offset="0.1349" stopColor="#757575" />
              <stop offset="0.3023" stopColor="#A0A0A0" />
              <stop offset="0.4331" stopColor="#BBBBBB" />
              <stop offset="0.51" stopColor="#C5C5C5" />
              <stop offset="0.5853" stopColor="#BABABA" />
              <stop offset="0.7164" stopColor="#9E9E9E" />
              <stop offset="0.8864" stopColor="#707070" />
              <stop offset="1" stopColor="#4D4D4D" />
            </linearGradient>
          </defs>
        </svg>
      </span>
      )} */}
      <div
        className={`story-preview-content ${
          frameless ? "is-frameless" : ""
        } ${isStoryScreenVisible ? "is-story" : ""} ${backgroundImage && !frameless ? "!p-0" : ""}`}
        // style={{ backgroundImage }}
      >
        {/* Both clips sit outside the status branch below, so answering — which
            swaps the whole screen for the loader — no longer tears them out of
            the DOM mid-play. They keep their refs across that swap, which is
            what lets `stopNarration` reach them at all; `node` outlives the
            screen, so they stay mounted for as long as there is one. Neither
            carries `controls`, so the UA stylesheet keeps them out of the
            layout. */}


        {backgroundImage ? ( 
          <>
          {frameless && (
          // z-index lifts the row above the full-card intro button, which
          // would otherwise take these clicks and start the story.
          <div className={`absolute top-5 left-6 z-10 flex items-center justify-between w-[93%] ${isFullscreen ? "mt-4" : ""}`}>
            <button
                  type="button"
                  className="story-preview-back-to-course"
                  onClick={handleBackToCourse}
                >
                  <i className="fa-solid fa-chevron-left"></i> Back
                </button>
            {headerAction}
          </div>)}
          <div className = "intro-img-container">
            <img
              className="intro-img"
              src={backgroundImage}
              alt="Story Picture"
              />
          </div>
              </>) : <>
          { frameless && <>
          { (
            <div className={`flex justify-between items-center gap-4 w-full ${isFullscreen ? "mt-5" : ""}`}>
              <button
                type="button"
                className="story-preview-back-to-course"
                onClick={handleBackToCourse}
                >
                <i className="fa-solid fa-chevron-left"></i> Back
              </button>
              {headerAction}
            </div>
            )}
          </>}
              </>}

        
        {node?.audio_url && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            ref={audioRef}
            src={node.audio_url}
            onTimeUpdate={handleTimeUpdate}
            onPlay={() => setIsNarrationPlaying(true)}
            onPause={() => setIsNarrationPlaying(false)}
            // Leave the whole passage highlighted once it finishes, then
            // run straight on into the options.
            onEnded={handleNarrationEnded}
            preload="auto"
          />
        )}
        {optionAudioQueue.length > 0 && (
          // The option lines share one element — its `src` is swapped per
          // clip so they play back to back, on from the question audio.
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio
            ref={optionAudioRef}
            onPlay={() => setIsOptionAudioPlaying(true)}
            onPause={() => setIsOptionAudioPlaying(false)}
            onEnded={handleOptionAudioEnded}
            // A missing/broken clip must not stall the run.
            onError={handleOptionAudioEnded}
            preload="auto"
          />
        )}
        {socketError ? (
          <div className="story-preview-error">{socketError}</div>
        ) : isWaitingForIntro || isWaitingForImages ? (
          loader
        ) : status === LowerSchoolStoryStatus.GENERATING ? (
          loader
        ) : status === LowerSchoolStoryStatus.NODE ? (
          <div className="story-preview-node">
            <div className="story-preview-narration">
              <Tooltip
                title={isAudioPlaying ? "Sound Off" : "Sound On"}
                placement="top"
                arrow
              >
                {/* Wrapped: a disabled button fires no events, so the
                    tooltip needs a live element to hang off. */}
                <span className="story-preview-audio-btn-wrap">
                  <button
                    type="button"
                    className={`story-preview-audio-btn ${
                      isAudioPlaying ? "" : "is-muted"
                    }`}
                    onClick={toggleNarration}
                    disabled={!node?.audio_url && !optionAudioQueue.length}
                    aria-label={isAudioPlaying ? "Sound Off" : "Sound On"}
                  >
                    {isAudioPlaying ? (
                      <VolumeUpRoundedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <VolumeOffRoundedIcon sx={{ fontSize: 18 }} />
                    )}
                  </button>
                </span>
              </Tooltip>

              <p className="story-preview-substory">
                {words.map((word, index) => (
                  <span
                    // Words repeat, so the index is the only stable key here.
                    key={`${word}-${index}`}
                    className={
                      index <= activeWordIndex
                        ? "story-preview-word is-spoken"
                        : "story-preview-word"
                    }
                  >
                    {word}{" "}
                  </span>
                ))}
              </p>
            </div>

            {isAudioPending && (
              <span className="story-preview-pending">
                Narration is still being generated…
              </span>
            )}

            {/* An advance-only screen carries a single option — it sits alone,
                centred, instead of filling one half of the two-up grid. */}
            <div
              className={`story-preview-options ${
                options.length === 1 ? "is-single" : ""
              }`}
              ref={optionsRef}
            >
              {options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`story-preview-option ${
                    selectedOptionId === option.id ? "is-selected" : ""
                  } ${
                    narratingOptionId === option.id ? "is-narrating" : ""
                  }`}
                  onClick={() => handleAnswer(option.id)}
                  disabled={isAnswering}
                >
                  {option.image_url ? (
                    <img src={option.image_url} alt="" className= {
                    selectedOptionId === option.id ? "is-selected-option" : ""
                  } />
                  ) : isPendingStatus(option.image_status) ? (
                    <span className={`story-preview-option-image-pending ${
                    selectedOptionId === option.id ? "is-selected-option" : ""
                  }`}>
                      <CircularProgress size={18} sx={{ color: "#0068ff" }} />
                    </span>
                  ) : null}
                  <span className={`story-preview-option-text  ${
                    selectedOptionId === option.id ? "is-selected-option" : ""
                  }`}>
                    {option.text}
                  </span>
                </button>
              ))}
            </div>

            <div className="story-preview-node-footer ml-6">
              {/* {frameless && 
              <button
                type="button"
                className="story-preview-back-to-course"
                onClick={handleBackToCourse}
              >
                <i className="fa-solid fa-chevron-left"></i> Back to course
              </button>} */}
             <button
                type="button"
                className="story-preview-back"
                onClick={handleBack}
                disabled={!canGoBack || isAnswering || isGoingBack}
              >
                <ArrowBackRoundedIcon sx={{ fontSize: 16 }} /> Back
              </button>
            </div>
          </div>
        ) : status === LowerSchoolStoryStatus.ENDED ? (
          // The student submits from `endSlot`; the teacher preview has nothing
          // to submit, so it keeps the loader up while it closes itself.
          endSlot ?? (
            loader
          )
        ) : (
          // the lesson image (the content's background) is the title card and
          // the CTA both — the whole of it starts the story
          <button
            type="button"
            className="story-preview-intro"
            onClick={handleStartStory}
            aria-label="Start the story"
          />
        )}
      </div>
    </div>
  );
};

export default LowerSchoolStoryStage;
